import React from "react";
import { observable } from "mobx";
import { observer, Observer } from "mobx-react";
import { CardContent, Grid } from "@material-ui/core";
import { CustomerType, UploadCustomerDoc, NodeType, GetCustomerFiles } from "@graphql";
import locale from "@bonliva-crm/locale";
import { store as rootStore } from "@store";
import { CustomerDocumentTypes } from "@bonliva-crm/shared";
import { Navigation, WithNavigation } from "@navigation";
import { CustomerProfileLocation } from "@navigation/locations/CustomerProfileLocation";
import { CustomerEditorLocation } from "@navigation/locations/CustomerEditorLocation";
import { DnDLocalContainer } from "@containers/DnDContainer";
import { DocumentUploader } from "@containers/DocumentUploader/DocumentUploader";
import { Body, ErrorCard, Spinner, Topbar } from "@components";
import { ErrorBoundary, withErrorBoundary } from "@components/error-boundary";
import { CustomerSelectorDropin } from "@containers";
import CustomerEventLog from "@containers/EventLog/CustomerEventLog/CustomerEventLog";
import NodeCallList from "@containers/NodeCallList/NodeCallList";
import ProfileSection from "@components/ProfileSection";
import { Tabs, TabProps } from "@components/Tabs";
import { CustomerProfileStore } from "./CustomerProfileStore";
import { CustomerCardSubscriber } from "../CustomerCard/CustomerCardSubscriber";
import CustomerProfileLayout from "./CustomerProfileLayout";
import { CustomerChildren } from "../CustomerChildren/CustomerChildren";
import { CustomerProfileContactPersons } from "./CustomerProfileContactPersons";
import { CustomerTopbar } from "../components/CustomerTopbar/CustomerTopbar";
import { CustomerRecommendedPrice } from "../CustomerRecommendedPrice/CustomerRecommendedPrice";
import { CustomerDocuments } from "./CustomerDocuments/CustomerDocuments";
import { CustomerSettings } from "./CustomerSettings/CustomerSettings";
import { SearchProfile } from "./SearchProfile/SearchProfile";
import { ConsultantMatching } from "./consultant-matching/consultant-matching";
import { SearchSelect } from "@common/Select/SearchSelect";

type Props = {
  customerGroup?: string;
  id: string;
  directUri: string;
};

@observer
class CustomerProfile extends React.Component<Props> {
  @observable store = new CustomerProfileStore(this.props.id, this.props.customerGroup);

  rootStore = rootStore();

  componentDidUpdate(prevProps: Props) {
    if (
      prevProps.id !== this.props.id ||
      prevProps.customerGroup !== this.props.customerGroup
    ) {
      this.store = new CustomerProfileStore(this.props.id, this.props.customerGroup);
    }
  }

  handleUpload = async (documentType: string, files: File[]) => {
    for (const file of files) {
      await UploadCustomerDoc.func(
        {
          id: this.props.id,
          file,
          documentType: documentType as CustomerDocumentTypes
        },
        { refetchQueries: [GetCustomerFiles.OperationName] }
      ).then(() => this.rootStore.snackbars.success(`Uploaded`));
    }
  };

  request = () => {
    this.rootStore.upload.requestFileUpload({ multiple: true }).then((files) => {
      this.store.files = files;
    });
  };

  render() {
    const store = this.store;

    const { directUri, id } = this.props;
    const { profile } = store;

    if (store.isLoading) {
      return <Spinner fullHeight />;
    }

    if (store.error) {
      return this.renderError(store.error);
    }

    const isRegion = profile.Type === CustomerType.Region;

    const docTypeOptions = [
      CustomerDocumentTypes.StandingCertificateForTR,
      CustomerDocumentTypes.Other
    ];

    rootStore().ui.unblockHistory("contact_person_form");

    return (
      <WithNavigation>
        {(navigation: Navigation) => (
          <Observer>
            {() => (
              <DnDLocalContainer
                multiple
                onDrop={(f) => {
                  this.store.files = f;
                }}
                noClick
                // disabled={tab === "hospitality"}
                disabled={this.store.openUploader}
              >
                <DocumentUploader
                  documentTypes={docTypeOptions.toLabelValue(
                    (v) => locale.customerDocumentTypes[v]
                  )}
                  files={this.store.files}
                  onUpload={(docType) => this.handleUpload(docType, this.store.files)}
                  onClose={() => {
                    this.store.files = [];
                  }}
                />
                <CustomerProfileLayout
                  id={id}
                  loading={store.isMoving}
                  topbar={
                    <CustomerTopbar
                      rightLink={directUri}
                      rightLinkText={store.profile.Name}
                      customerParents={store.profile.Parents}
                    />
                  }
                  side={this.renderSidebar()}
                  main={
                    <>
                      <ErrorBoundary>
                        <CustomerCardSubscriber
                          customerGroup={this.props.customerGroup}
                          id={this.store.id}
                          onCloseGroup={() =>
                            navigation.navigate(new CustomerProfileLocation(id))
                          }
                          onDelete={() => store.deleteCustomer()}
                          onMove={store.moveCustomer}
                          onPromoteToOrg={
                            store.canPromoteToOrg ? store.promoteToOrg : null
                          }
                          cantMoveCustomersReasons={store.customerCantBeMovedReasons}
                          onEdit={() =>
                            navigation.navigate(new CustomerEditorLocation(this.store.id))
                          }
                          onClick={null}
                          onParentClick={(_id) =>
                            navigation.navigate(new CustomerProfileLocation(_id))
                          }
                          withAvatarUpload
                        />
                      </ErrorBoundary>

                      {store.profile.Description && (
                        <ErrorBoundary>
                          <ProfileSection
                            title={locale.AboutUs}
                            fitted
                            collapsible={false}
                          >
                            <CardContent>
                              <Body newlineToBr>{store.profile.Description}</Body>
                            </CardContent>
                          </ProfileSection>
                        </ErrorBoundary>
                      )}

                      {!isRegion && this.renderAreasOfExpertise()}

                      <ErrorBoundary>
                        <CustomerProfileContactPersons store={store} />
                      </ErrorBoundary>

                      {withErrorBoundary(this.renderTabs())}
                    </>
                  }
                />

                <CustomerSelectorDropin storeRef={store.customerSelectorRef.register} />
              </DnDLocalContainer>
            )}
          </Observer>
        )}
      </WithNavigation>
    );
  }

  private renderTabs = () => {
    const { tab, openTab, openUpload } = this.store;
    const tabs: TabProps[] = [
      {
        label: locale.ActionLog,
        value: "events",
        content: (
          <ErrorBoundary>
            <CustomerEventLog
              commentBoxMeta={{
                AreasOfExpertise: this.store.selectedAreasOfExpertise.slice(),
                ContactPersonId: this.store.selectedContactPersonIds,
                RootCustomerId: this.store.rootCustomerId
              }}
              commentBoxHeaderProps={{
                areasOfExpertise: this.store.selectedAreasOfExpertise.slice(),
                contactPersons: this.store.selectedContactPersons.slice().toLabelValue(
                  (c) => c.Name,
                  (c) => c._id
                ),
                onRemoveAreaOfExpertise: this.store.handleAreaOfExpertiseSelect,
                onRemoveContactPerson: this.store.handleContactPersonSelect
              }}
              store={this.store.customerEventLogStore}
              onUpload={openUpload}
              uploadDisabled={false}
            />
          </ErrorBoundary>
        )
      },
      {
        label: locale.Files,
        value: "documents",
        content: () => (
          <CustomerDocuments
            id={this.props.id}
            onUpload={this.request}
            allowDelete={!this.store.profile.noApproval}
          />
        )
      },
      {
        label: locale.GDPR,
        value: "gdpr",
        content: null
      }
    ];

    if (
      [CustomerType.Hospital, CustomerType.Department].includes(this.store.profile.Type)
    ) {
      tabs.push({
        label: locale.matching,
        value: "matching",
        content: <ConsultantMatching customerId={this.props.id} />
      });
    }

    if (
      rootStore().user.can("customer:recommended-prices:all") &&
      this.store.profile.Type !== CustomerType.Region
    ) {
      tabs.push({
        label: "Rekommenderad Lön",
        value: "recommended_price",
        content: <CustomerRecommendedPrice customerId={this.props.id} />
      });
    }

    return (
      <Tabs
        tabs={tabs}
        value={tab}
        onChange={(_, v) => openTab(v)}
        withHref
        TabsProps={{ indicatorColor: "primary" }}
        AppBarProps={{
          style: { borderTopRightRadius: 4, borderTopLeftRadius: 4 }
        }}
      />
    );
  };

  private renderSidebar = () => {
    const { customerGroup } = this.props;
    const { profile } = this.store;

    const childrenListTitle =
      profile.Type === CustomerType.Region ? locale.Hospital : locale.Departments;

    return (
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <ErrorBoundary>
            <ProfileSection
              title={locale.TimeReportSwitchLabel}
              fitted
              collapsible
              style={{ flex: "1 0 100%" }}
            >
              <CustomerSettings store={this.store} />
            </ProfileSection>
          </ErrorBoundary>
        </Grid>

        <Grid item xs={12}>
          <ErrorBoundary>
            <ProfileSection
              title={locale.SearchProfiles}
              fitted
              collapsible
              style={{ flex: "1 0 100%" }}
            >
              <SearchProfile store={this.store} />
            </ProfileSection>
          </ErrorBoundary>
        </Grid>

        <Grid item xs={12}>
          <ErrorBoundary>
            <ProfileSection
              title="Ring- & Kontaktlistor"
              fitted
              collapsible
              style={{ flex: "1 0 100%" }}
            >
              <NodeCallList
                node={{
                  NodeID: this.props.id,
                  NodeType: NodeType.Customer,
                  Name: this.store.profile.Name
                }}
              />
            </ProfileSection>
          </ErrorBoundary>
        </Grid>

        {profile.Type !== CustomerType.Department && (
          <ErrorBoundary>
            <WithNavigation>
              {({ navigate }) => (
                <CustomerChildren
                  filterGroups={(group) =>
                    customerGroup ? group === customerGroup : true
                  }
                  id={this.props.id}
                  onAddCustomer={this.store.addCustomer}
                  onAddContactPerson={(group) =>
                    this.store.openContactPersonEditor(null, { group })
                  }
                  onClick={(id) => navigate(new CustomerProfileLocation(id))}
                  onGroupHeaderClick={(group) =>
                    group
                      ? navigate(new CustomerProfileLocation(profile._id, group))
                      : null
                  }
                  title={childrenListTitle}
                  renderGroup={(content, name) => (
                    <Grid item xs={12} key={name}>
                      {content}
                    </Grid>
                  )}
                />
              )}
            </WithNavigation>
          </ErrorBoundary>
        )}
      </Grid>
    );
  };

  private renderAreasOfExpertise = () => {
    return (
      <div data-test="CustomerAreasOfExpertiseList">
        <SearchSelect
          label={locale.Expertise}
          options={this.store.contactPersonsAoe.sortBy((x) => x)}
          value={this.store.selectedAreasOfExpertise}
          onChange={(v) => {
            this.store.selectedAreasOfExpertise = v;
          }}
        />
      </div>
    );
  };

  private renderError = (err: any) => {
    return (
      <div>
        <Topbar />
        <ErrorCard centered title="Error" message={err.toString()} />
      </div>
    );
  };
}

export default CustomerProfile;
